import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { AGGClient } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import { Button } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { getIsSMBSalesLead } from "./contact-sales-cta-helpers";

const fullWidthStyles = css({
    maxWidth: "none",
    width: "100%"
});

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const { accountId } = useLoggedInUser();
    // feature gate has configuration to only be enabled on specified page paths
    // https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/wac_sales_inbound_leads
    const isFeatureGateEnabled = checkFeatureFlagGate(
        featureFlags.wacSalesInboundLeads
    );

    const [isSalesLead, setIsSalesLead] = useState<boolean>(false);

    useEffect(() => {
        if (!isFeatureGateEnabled || !accountId) return;
        const aggClient = new AGGClient({
            gatewayUrl:
                process.env["NODE_ENV"] === "production"
                    ? "https://www.atlassian.com/gateway/api/graphql"
                    : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
        });

        const isSMBSalesLead = async () => {
            getIsSMBSalesLead({
                accountId,
                aggClient
            })
                .then((response) => {
                    setIsSalesLead(!!response);
                })
                .catch(() => {
                    console.error(
                        "Error fetching GUPS user profile for SMB Sales lead"
                    );
                });
        };
        isSMBSalesLead();
    }, [accountId, isFeatureGateEnabled]);

    if (!accountId || !isFeatureGateEnabled) {
        return;
    }

    return isSalesLead && <CtaButtonRenderer isFullWidth={isFullWidth} />;
};

const CtaButtonRenderer = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const localizedHref = useLocalizedHref(
        "/enterprise/contact?formType=product-features"
    );

    useEffect(() => {
        if (analyticsClient) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "button",
                actionSubjectId: "contactSalesCta",
                source: "globalNav",
                attributes: {
                    isMobile: !!isFullWidth
                }
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent, isFullWidth]);

    return (
        <Button
            variant="primary_alt"
            css={isFullWidth && fullWidthStyles}
            href={localizedHref}
        >
            <FormattedMessage
                id="global-nav.contact-sales-cta.label"
                defaultMessage="Contact us"
            />
        </Button>
    );
};
